<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useDishFields } from '@/composition/menu-import/use-dish-fields';
  import { DRAFT_DESCRIPTION_MAX } from '@/domain/menu-import/item-editor';
  import { liveLineModel } from '@/domain/menu-import/live-rows';
  import type { ILiveLineAction } from '@/domain/menu-import/live-rows';
  import type { TDishField } from '@/domain/menu-import/item-editor';
  import {
    itemReviewText,
    reviewLineText,
    sourceCrop,
    unconfirmedAllergenKeys,
  } from '@/domain/menu-import/review-queue';
  import { DRAFT_NAME_MAX } from '@/domain/menu-import/workspace-view';
  import { formatMoney } from '@/domain/store-setup/money';
  import type {
    IDraftItem,
    ILiveOther,
    IMenuImportPage,
    TDraftOp,
  } from '@/interfaces/IMenuImport';
  import { Globals } from '@/modules/Globals';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2MenuAllergenChips from '../editor/V2MenuAllergenChips.vue';
  import V2MenuSizesEditor from '../editor/V2MenuSizesEditor.vue';
  import V2MenuLiveLine from './V2MenuLiveLine.vue';
  import V2MenuLivePriceFields from './V2MenuLivePriceFields.vue';

  /**
   * One dish of «Revisar uno por uno» (plan §4.5.4): why it needs a look
   * (with the allergens read while they are not confirmed), «Leímos esto:»
   * with the menu photo cropped around it, its name, price or sizes,
   * description and, for unconfirmed allergens, the allergen chips to
   * correct, then «Está bien» (the dish as shown is right, corrections
   * included), «Guardar cambio» (only the corrections) or «Saltar». Nothing
   * is saved until one of them is tapped. In an update (plan §3.5) a dish to
   * decide gets the live line instead of «Está bien», and a dish already on
   * the menu shows only its prices (the rest of it never changes).
   */
  const props = withDefaults(
    defineProps<{
      item: IDraftItem;
      pages: IMenuImportPage[];
      currency: string;
      country: string;
      /** Update sessions: the menu's dishes no dish is linked to (`draft.live.others`); else null. */
      liveOthers?: ILiveOther[] | null;
    }>(),
    { liveOthers: null }
  );
  const emit = defineEmits<{
    /** «Guardar cambio»: the corrections. */
    (e: 'save', ops: TDraftOp[]): void;
    /** «Está bien»: the corrections to save with the confirmation (maybe none). */
    (e: 'confirm', ops: TDraftOp[]): void;
    (e: 'skip'): void;
    /** A live-line button other than «Usar» / «Está bien» (those confirm). */
    (e: 'live', action: ILiveLineAction): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const uid = nextDomId('menu-queue-item');
  const nameId = `${uid}-name`;
  const descriptionId = `${uid}-description`;
  const allergensLabelId = `${uid}-allergens`;

  const fields = useDishFields({
    item: () => props.item,
    country: () => props.country,
  });
  const { form, errors, hasChanges } = fields;

  const root = ref<HTMLElement | null>(null);
  /** The menu's name of a dish already on it (only its prices can change); else null. */
  const currentLinkedName = computed(() => props.item.live?.dish?.name ?? null);
  /**
   * The card's fields follow the link, but never swap under the owner's
   * typing (the phone keyboard would close): a link made elsewhere applies
   * once the focus leaves the card.
   */
  const linkedName = ref(currentLinkedName.value);
  let linkSwapPending = false;

  function focusInside(target: EventTarget | null): boolean {
    return target instanceof Node && !!root.value?.contains(target);
  }

  watch(currentLinkedName, (next) => {
    if (focusInside(document.activeElement)) linkSwapPending = true;
    else linkedName.value = next;
  });

  function onFocusOut(event: FocusEvent) {
    if (!linkSwapPending || focusInside(event.relatedTarget)) return;
    linkSwapPending = false;
    linkedName.value = currentLinkedName.value;
  }
  const liveLine = computed(() =>
    props.liveOthers === null
      ? null
      : liveLineModel(props.item, props.liveOthers, {
          money: (value) => formatMoney(value, props.currency, props.country),
        })
  );

  //#region Allergens (saved with «Está bien» or «Guardar cambio», like the fields)
  /** The chips show for a dish whose allergens wait for a confirmation; once shown they stay (never for a dish of the menu). */
  const allergensShown = ref(
    linkedName.value === null && unconfirmedAllergenKeys(props.item).length > 0
  );
  /** The chips as the owner left them; they follow the server until touched. */
  const allergens = ref(props.item.allergens);
  const allergensTouched = ref(false);
  const allergensChanged = computed(
    () => allergensTouched.value && allergens.value !== props.item.allergens
  );

  watch(
    () =>
      linkedName.value === null &&
      unconfirmedAllergenKeys(props.item).length > 0,
    (unconfirmed) => {
      if (unconfirmed) allergensShown.value = true;
    }
  );
  watch(
    () => props.item.allergens,
    (next) => {
      if (!allergensTouched.value) allergens.value = next;
    }
  );

  function setAllergens(bits: number) {
    allergens.value = bits;
    allergensTouched.value = true;
  }

  /** The chips' correction (the API then marks the allergens confirmed). */
  function allergenOps(): TDraftOp[] {
    return allergensChanged.value
      ? [
          {
            op: 'updateItem',
            itemId: props.item.id,
            allergens: allergens.value,
          },
        ]
      : [];
  }
  //#endregion

  const review = computed(() => itemReviewText(props.item));
  const reviewLines = computed(() => {
    const text = review.value;
    if (text === null) return [];
    return text.lines.map((line) =>
      reviewLineText(line, tr, proxy.$i18n.locale)
    );
  });
  const canSave = computed(() => hasChanges.value || allergensChanged.value);
  /** A red dish is fixed by correcting it; «Está bien» is for the amber ones the live line does not settle. */
  const canConfirm = computed(
    () => props.item.review.level === 'check' && liveLine.value === null
  );
  const crop = computed(() => sourceCrop(props.item, props.pages));
  const cropStyle = computed(() => {
    const current = crop.value;
    if (!current) return null;
    const url = `${Globals.uploadsPath}/${current.storageKey}`;
    return {
      backgroundImage: `url(${JSON.stringify(url)})`,
      backgroundSize: current.size,
      backgroundPosition: current.position,
      paddingTop: `${Math.round(current.ratio * 10000) / 100}%`,
    };
  });

  function onFocus(field: TDishField) {
    fields.focusField(field);
  }

  function onBlur(field: TDishField) {
    fields.blurField(field);
  }

  function onPriceSizesFocus(inside: boolean) {
    if (inside) onFocus('priceSizes');
    else onBlur('priceSizes');
  }

  function save() {
    const result = fields.commit();
    if (result.status !== 'valid') return;
    const ops = [...result.ops, ...allergenOps()];
    if (ops.length === 0) return;
    allergensTouched.value = false;
    emit('save', ops);
  }

  function confirm() {
    const result = fields.commit();
    if (result.status !== 'valid') return;
    const ops = [...result.ops, ...allergenOps()];
    allergensTouched.value = false;
    emit('confirm', ops);
  }

  /**
   * The live line's buttons. «Usar {price}» and «Está bien» confirm exactly
   * what they say (the price on the button, not a typed one); a link or «es
   * otro» first saves what the owner typed (a price for the new dish);
   * «Dejar» drops the photo's price, typed ones included.
   */
  function onLiveAction(action: ILiveLineAction) {
    if (action.kind === 'confirm') {
      allergensTouched.value = false;
      emit('confirm', []);
      return;
    }
    if (action.kind === 'link' || action.kind === 'new') {
      const result = fields.commit();
      if (result.status !== 'valid') return;
      const ops = [...result.ops, ...allergenOps()];
      allergensTouched.value = false;
      if (ops.length > 0) emit('save', ops);
    }
    emit('live', action);
  }
</script>

<template>
  <article
    ref="root"
    class="v2-menu-review-queue-item"
    :aria-label="linkedName !== null ? linkedName : item.name"
    @focusout="onFocusOut"
  >
    <V2MenuLiveLine v-if="liveLine" :model="liveLine" @action="onLiveAction" />
    <p v-if="review" class="item-status" :class="`is-${review.tone}`">
      <span class="status-dot" aria-hidden="true" />
      <span class="status-text">
        <span v-for="line in reviewLines" :key="line" class="status-line">{{
          line
        }}</span>
      </span>
    </p>
    <div v-if="crop && cropStyle" class="item-source">
      <p class="source-label">{{ $t('menuImport.queue.weRead') }}</p>
      <div
        class="source-frame"
        role="img"
        :aria-label="item.name"
        :style="cropStyle"
      />
    </div>
    <div v-if="linkedName !== null" class="item-fields">
      <div class="linked-head">
        <p class="linked-name">{{ linkedName }}</p>
        <p class="linked-text">{{ $t('menuImport.update.editorHeader') }}</p>
      </div>
      <V2MenuLivePriceFields
        :item="item"
        :value="form.priceSizes"
        :errors="errors.priceSizes"
        :currency="currency"
        :country="country"
        @input="fields.setPriceSizes"
        @focus-change="onPriceSizesFocus"
      />
    </div>
    <div v-else class="item-fields">
      <PosUiField
        :label="$t('menuImport.editor.name')"
        :input-id="nameId"
        :error="errors.name ? $t('menuImport.editor.nameRequired') : undefined"
      >
        <PosUiInput
          :id="nameId"
          :value="form.name"
          :maxlength="DRAFT_NAME_MAX"
          autocomplete="off"
          @input="fields.setName"
          @focus="onFocus('name')"
          @blur="onBlur('name')"
        />
      </PosUiField>
      <V2MenuSizesEditor
        :value="form.priceSizes"
        :errors="errors.priceSizes"
        :currency="currency"
        @input="fields.setPriceSizes"
        @focus-change="onPriceSizesFocus"
      />
      <PosUiField
        :label="$t('menuImport.editor.description')"
        :input-id="descriptionId"
      >
        <PosUiTextarea
          :id="descriptionId"
          :value="form.description"
          :rows="3"
          :maxlength="DRAFT_DESCRIPTION_MAX"
          @input="fields.setDescription"
          @focus.native="onFocus('description')"
          @blur.native="onBlur('description')"
        />
      </PosUiField>
      <div v-if="allergensShown" class="item-allergens">
        <p :id="allergensLabelId" class="allergens-label">
          {{ $t('menuImport.editor.allergens') }}
        </p>
        <V2MenuAllergenChips
          :value="allergens"
          :labelled-by="allergensLabelId"
          @input="setAllergens"
        />
        <p class="allergens-hint">
          {{ $t('menuImport.editor.allergensHint') }}
        </p>
      </div>
    </div>
    <div class="item-actions">
      <PosUiButton
        v-if="canConfirm"
        color="success"
        variant="soft"
        icon="check"
        @click="confirm"
        >{{ $t('menuImport.item.looksGood') }}</PosUiButton
      >
      <PosUiButton v-if="canSave" color="neutral" @click="save">{{
        $t('menuImport.queue.saveChange')
      }}</PosUiButton>
      <button type="button" class="item-skip" @click="emit('skip')">
        {{ $t('menuImport.queue.skip') }}
      </button>
    </div>
  </article>
</template>

<style scoped lang="scss">
  .v2-menu-review-queue-item {
    @apply tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;

    .item-status {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium;
      .status-dot {
        @apply tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full;
      }
      &.is-check {
        @apply tw-text-amber-700;
        .status-dot {
          @apply tw-bg-amber-500;
        }
      }
      &.is-fix {
        @apply tw-text-red-700;
        .status-dot {
          @apply tw-bg-red-500;
        }
      }
      // One line per thing to check (the reason, the allergens read).
      .status-text {
        @apply tw-flex tw-min-w-0 tw-flex-col;
      }
    }
    .item-allergens {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
      .allergens-label {
        @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
      }
      .allergens-hint {
        @apply tw-m-0 tw-text-xs tw-text-neutral-500;
      }
    }
    .item-source {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .source-label {
      @apply tw-m-0 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    // The page photo as a background: `background-size` / `-position` frame the dish.
    .source-frame {
      @apply tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-100 tw-bg-no-repeat;
      height: 0;
    }
    .item-fields {
      @apply tw-flex tw-flex-col tw-gap-4;
    }
    .linked-head {
      @apply tw-flex tw-flex-col tw-gap-0.5;
    }
    .linked-name {
      @apply tw-m-0 tw-break-words tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .linked-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    .item-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3;
    }
    .item-skip {
      @apply tw-ml-auto tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-2 tw-text-sm tw-font-medium tw-text-neutral-600 tw-underline tw-underline-offset-4 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
